<template>
  <router-link
    v-slot="{ navigate }"
    :to="link"
    custom
  >
    <div
      class="oct-image"
      :class="{ 'cursor-pointer': link }"
      :style="{ backgroundImage: `url(${backgroundImg})` }"
      :title="title"
      :data-ab-id="dataAbId"
      @click="navigate"
    >
      <div class="container">
        <span>{{ topTitle }}</span>
        <h2 v-if="title">
          {{ title }}
        </h2>
        <p>{{ subText }}</p>
        <SharedTargetLink
          v-if="link && ctaText"
          :url="link"
          :target="ctaTargetMode"
        >
          {{ ctaText }}
        </SharedTargetLink>
      </div>
    </div>
  </router-link>
</template>

<script lang="ts" setup>
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  image?: { '@path': string };
  topTitle?: string;
  topTitleColor?: string;
  title?: string;
  titleColor?: string;
  subText?: string;
  subTextColor?: string;
  ctaText?: string;
  ctaTargetMode?: string;
  pageLink?: any;
  dataAbId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  image: () => ({ '@path': '' }),
  title: '',
  titleColor: '',
  topTitle: '',
  topTitleColor: '',
  subText: '',
  subTextColor: '',
  ctaText: '',
  ctaTargetMode: ' _self',
  pageLink: () => ({}),
});

const { image, pageLink } = toRefs(props);

const { $domain } = useNuxtApp();

const link = computed(() => magnoliaGetters.getLink(pageLink.value, pageLink.value?.intLinkParam));
const backgroundImg = computed(() => magnoliaGetters.getDefaultSrcAsset(image.value, $domain.cmsAssetDomainPath));
</script>

<style lang="scss" scoped>
  .oct-image {
      border: none;
      margin: var(--spacer-base) 0;
      display: flex;
      justify-content: center;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      align-items: center;
      padding: 50px 110px;
      width: auto;

      //Mobile
      @media screen and (max-width: $mobile-max) {
          padding: 20px 20px;
      }
      //Tablet
      @media (min-width: $tablet-min) and (max-width: $tablet-max) {
          padding: 50px 80px;
      }
  }

  .container {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      position: relative;
      background: var(--c-white);
      width: 100%;
      padding: 30px 130px;

      //Mobile
      @media screen and (max-width: $mobile-max) {
          padding: 20px;
      }
      //Tablet
      @media (min-width: $tablet-min) and (max-width: $tablet-max) {
          padding: 30px 54px;
      }
  }

  span {
      color: v-bind(topTitleColor);
      font-size: var(--font-size--sm);
      font-weight: bold;
      letter-spacing: 1px;
      text-transform: uppercase;

      //Mobile
      @media screen and (max-width: $mobile-max) {
          font-size: var(--font-size--xs);
          letter-spacing: 0.86px;
      }
  }

  h2 {
      color: v-bind(titleColor);
      font-size: 30px;
      font-weight: bold;
      letter-spacing: 0.22px;
      line-height: 41px;

      //Mobile
      @media screen and (max-width: $mobile-max) {
          font-size: 20px;
          letter-spacing: 0.15px;
          line-height: 27px;
      }
      //Tablet
      @media (min-width: $tablet-min) and (max-width: $tablet-max) {
          font-size: var(--h1-font-size);
          letter-spacing: 0px;
          line-height: 33px;
      }
  }

  p {
      color: v-bind(subTextColor);
      margin: 10px 0 0 0;
      font-size: var(--font-size--base);
      letter-spacing: 0.5px;
      line-height: 22px;

      //Mobile
      @media screen and (max-width: $mobile-max) {
          margin-top: 5px;
          font-size: var(--font-size--sm);
          letter-spacing: 0.25px;
          line-height: 19px;
      }
  }

  a {
      margin-top: 25px;
      background: var(--c-white);
      padding: 10px 30px;
      color: var(--c-dark);
      border-radius: 3px;
      border: 1.33px solid var(--c-dark);
      box-shadow: 0 5px 20px 0 rgba(25, 25, 25, 0.08);
      font-size: var(--font-size--sm);
      font-weight: bold;
      letter-spacing: 1px;
      text-transform: uppercase;
      text-decoration: none;
      text-align: center;

      //Mobile
      @media screen and (max-width: $mobile-max) {
          margin-top: 15px;
          padding: 9px 0;
          width: 100%;
      }
      //Tablet
      @media (min-width: $tablet-min) and (max-width: $tablet-max) {
          margin-top: 20px;
      }
  }
</style>
